import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { LatLng } from 'leaflet';
import { Box, Button, TextField, Typography, Paper, IconButton } from '@mui/material';
import { MyLocation as MyLocationIcon, Clear as ClearIcon } from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix pour les icônes Leaflet par défaut
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface LocationPickerProps {
  latitude?: number | null;
  longitude?: number | null;
  location?: string;
  onLocationChange: (lat: number | null, lng: number | null, address?: string) => void;
  height?: string;
  center?: [number, number];
  zoom?: number;
}

// Composant pour gérer les clics sur la carte
function MapClickHandler({ onLocationChange }: { onLocationChange: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationChange(lat, lng);
    },
  });
  return null;
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude,
  longitude,
  location,
  onLocationChange,
  height = '400px',
  center = [6.3654, 2.4183], // Cotonou par défaut
  zoom = 13,
}) => {
  const [mapCenter, setMapCenter] = useState<[number, number]>(
    latitude && longitude ? [latitude, longitude] : center
  );
  const [currentLat, setCurrentLat] = useState<number | null>(latitude || null);
  const [currentLng, setCurrentLng] = useState<number | null>(longitude || null);
  const [address, setAddress] = useState<string>(location || '');
  const [isGeolocating, setIsGeolocating] = useState(false);

  useEffect(() => {
    if (latitude && longitude) {
      setCurrentLat(latitude);
      setCurrentLng(longitude);
      setMapCenter([latitude, longitude]);
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (location) {
      setAddress(location);
    }
  }, [location]);

  // Fonction pour obtenir l'adresse à partir des coordonnées (reverse geocoding)
  const reverseGeocode = async (lat: number, lng: number) => {
    try {
      // Vérifier que les coordonnées sont valides (Bénin approximativement: lat 6-12, lng 1-4)
      if (lat < 5 || lat > 13 || lng < 0 || lng > 5) {
        console.warn('Coordonnées hors du Bénin, utilisation du format simple');
        return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      }

      // Utiliser un zoom plus élevé pour une meilleure précision
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=fr&extratags=1`,
        {
          headers: {
            'User-Agent': 'SOBEBRA-Zenith Platform',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && data.display_name) {
        // Vérifier que l'adresse retournée est cohérente avec les coordonnées
        const returnedLat = parseFloat(data.lat);
        const returnedLng = parseFloat(data.lon);
        
        // Calculer la distance en degrés
        const latDiff = Math.abs(returnedLat - lat);
        const lngDiff = Math.abs(returnedLng - lng);
        const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
        
        // Construire une adresse précise en utilisant les coordonnées GPS exactes (pas celles retournées)
        let addressParts: string[] = [];
        if (data.address) {
          // Prioriser les éléments les plus spécifiques pour une adresse précise
          if (data.address.house_number) {
            addressParts.push(data.address.house_number);
          }
          if (data.address.road) {
            addressParts.push(data.address.road);
          }
          if (data.address.neighbourhood || data.address.suburb || data.address.quarter) {
            addressParts.push(data.address.neighbourhood || data.address.suburb || data.address.quarter);
          }
          if (data.address.city || data.address.town || data.address.municipality) {
            addressParts.push(data.address.city || data.address.town || data.address.municipality);
          }
        }
        
        // Utiliser l'adresse construite si disponible, sinon utiliser display_name
        let finalAddress: string;
        if (addressParts.length > 0) {
          finalAddress = addressParts.join(', ');
        } else {
          // Si pas d'adresse construite, utiliser display_name
          finalAddress = data.display_name || '';
        }
        
        // Toujours inclure les coordonnées GPS exactes pour garantir la précision
        // Format: "Adresse détaillée (6.123456, 2.123456)"
        const coordsStr = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        if (finalAddress && finalAddress.length > 5) {
          finalAddress = `${finalAddress} (${coordsStr})`;
        } else {
          finalAddress = coordsStr;
        }
        
        setAddress(finalAddress);
        return finalAddress;
      }
    } catch (error) {
      console.error('Erreur lors du reverse geocoding:', error);
    }
    // Fallback : retourner les coordonnées si le geocoding échoue
    const fallback = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    setAddress(fallback);
    return fallback;
  };

  const handleMapClick = async (lat: number, lng: number) => {
    setCurrentLat(lat);
    setCurrentLng(lng);
    setMapCenter([lat, lng]);
    const addr = await reverseGeocode(lat, lng);
    onLocationChange(lat, lng, addr);
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('La géolocalisation n\'est pas disponible sur votre appareil. Veuillez utiliser un navigateur moderne ou activer la géolocalisation dans les paramètres.');
      return;
    }

    setIsGeolocating(true);
    
    // Options pour améliorer la précision et gérer les erreurs
    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 secondes
      maximumAge: 0, // Ne pas utiliser de position en cache
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setCurrentLat(lat);
        setCurrentLng(lng);
        setMapCenter([lat, lng]);
        const addr = await reverseGeocode(lat, lng);
        onLocationChange(lat, lng, addr);
        setIsGeolocating(false);
      },
      (error) => {
        console.error('Erreur de géolocalisation:', error);
        let errorMessage = '';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'L\'accès à la géolocalisation a été refusé. Veuillez autoriser l\'accès à votre position dans les paramètres de votre navigateur et rafraîchir la page.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Les informations de position ne sont pas disponibles. Vérifiez que votre GPS est activé et que vous êtes dans une zone avec couverture réseau.';
            break;
          case error.TIMEOUT:
            errorMessage = 'La demande de position a expiré (10 secondes). Veuillez réessayer. Assurez-vous d\'être dans une zone avec bonne réception GPS.';
            break;
          default:
            errorMessage += 'Une erreur inconnue s\'est produite.';
            break;
        }
        
        alert(errorMessage);
        setIsGeolocating(false);
      },
      options
    );
  };

  const handleClearLocation = () => {
    setCurrentLat(null);
    setCurrentLng(null);
    setAddress('');
    onLocationChange(null, null, '');
  };

  const handleAddressChange = (newAddress: string) => {
    setAddress(newAddress);
  };

  const handleSearchAddress = async () => {
    if (!address.trim()) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
        {
          headers: {
            'User-Agent': 'SOBEBRA-Zenith Platform',
          },
        }
      );
      const data = await response.json();
      if (data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        setCurrentLat(lat);
        setCurrentLng(lng);
        setMapCenter([lat, lng]);
        onLocationChange(lat, lng, data[0].display_name);
      } else {
        alert('Adresse introuvable');
      }
    } catch (error) {
      console.error('Erreur lors de la recherche d\'adresse:', error);
      alert('Erreur lors de la recherche d\'adresse');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          label="Adresse"
          value={address}
          onChange={(e) => handleAddressChange(e.target.value)}
          placeholder="Rechercher une adresse ou cliquer sur la carte"
          size="small"
        />
        <Button
          variant="outlined"
          onClick={handleSearchAddress}
          disabled={!address.trim()}
          sx={{ minWidth: 100 }}
        >
          Rechercher
        </Button>
        <Button
          variant="outlined"
          startIcon={<MyLocationIcon />}
          onClick={handleGetCurrentLocation}
          disabled={isGeolocating}
          sx={{ minWidth: 150 }}
        >
          {isGeolocating ? 'Localisation...' : 'Ma position'}
        </Button>
        {(currentLat || currentLng) && (
          <IconButton onClick={handleClearLocation} color="error" size="small">
            <ClearIcon />
          </IconButton>
        )}
      </Box>

      <Paper sx={{ overflow: 'hidden', border: '1px solid #e0e0e0' }}>
        <Box sx={{ height, width: '100%', position: 'relative' }}>
          <MapContainer
            center={mapCenter}
            zoom={zoom}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {currentLat && currentLng && (
              <Marker position={[currentLat, currentLng]} />
            )}
            <MapClickHandler onLocationChange={handleMapClick} />
          </MapContainer>
        </Box>
      </Paper>

      {(currentLat || currentLng) && (
        <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Latitude:</strong> {currentLat?.toFixed(6)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Longitude:</strong> {currentLng?.toFixed(6)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LocationPicker;



